import React from 'react';
import Box from '@mui/material/Box';
import { AppBar, Collapse, CircularProgress, Divider, Menu, MenuItem } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutButton from './LogoutButton';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import FeedbackButton from './buttons/FeedbackButton';
import SupportButton from './buttons/SupportButton';
import { useUserContext } from '../context/userConext';
import { RolesEnum, RoleHierarchy } from '../types/roleTypes';
import { DurationEnum, ActionType, RoleType, BreakdownTab, ALL } from '../types/analyticTypes';

function getHighestRole(roles: string[]): string {
  const validRoles = roles
    .map((role) => RolesEnum[role as keyof typeof RolesEnum])
    .filter((role) => role);

  return validRoles.sort((a, b) => RoleHierarchy[b] - RoleHierarchy[a]).shift() as RolesEnum;
}

function Header() {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [analyticsOpen, setAnalyticsOpen] = React.useState(false);
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { user_roles } = useUserContext();

  const userRole: string = getHighestRole(user_roles);

  if (isLoading) {
    return <CircularProgress />;
  }

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
    setAnalyticsOpen(false);
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: 'black', width: '100%', marginBottom: '1%' }}>
      <Box sx={{ margin: 'auto', width: '80%' }}>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Link to={'/'}>
            <Box
              component="img"
              sx={{ width: 150 }}
              alt="WeWork"
              src={window.location.origin + '/logo.png'}
            />
          </Link>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box>
              <SupportButton />
            </Box>
            <Box>
              <FeedbackButton />
            </Box>
            <Box sx={{ flexGrow: 0, textAlign: 'left' }}>
              <Tooltip title="Open settings">
                <Box onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  Logged in as {user?.name}
                </Box>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                autoFocus={false}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                slotProps={{ paper: { sx: { width: 240 } } }}
              >
                <MenuItem key="info" disabled>
                  <Typography component={'div'} textAlign="center">
                    role: {userRole}
                  </Typography>
                </MenuItem>
                {userRole === RolesEnum.ncc_admin && (
                  <>
                    <MenuItem
                      key="users"
                      onClick={handleCloseUserMenu}
                      component={Link}
                      to="/management/users"
                    >
                      <Typography component={'div'} textAlign="center">
                        User List
                      </Typography>
                    </MenuItem>
                    <MenuItem
                      key="analytics-parent"
                      onClick={() => setAnalyticsOpen((prev) => !prev)}
                      sx={{ justifyContent: 'space-between', gap: 1 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BarChartIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography component={'div'}>Analytics</Typography>
                      </Box>
                      {analyticsOpen ? (
                        <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      ) : (
                        <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      )}
                    </MenuItem>
                    <Collapse in={analyticsOpen} timeout="auto" unmountOnExit>
                      <MenuItem
                        key="analytics"
                        onClick={handleCloseUserMenu}
                        component={Link}
                        to={`/management/analytics?duration=${DurationEnum.day}&actions=${ActionType.all}&role=${RoleType.all}&users=${ALL}`}
                        sx={{ pl: 4 }}
                      >
                        <Typography component={'div'} textAlign="center">
                          Overview
                        </Typography>
                      </MenuItem>
                      <MenuItem
                        key="analytics-breakdown"
                        onClick={handleCloseUserMenu}
                        component={Link}
                        to={`/management/analytics-breakdown?duration=${DurationEnum.day}&actions=${ActionType.all}&role=${RoleType.all}&users=${ALL}&tab=${BreakdownTab.role}&top_n=10`}
                        sx={{ pl: 4 }}
                      >
                        <Typography component={'div'} textAlign="center">
                          Breakdown by Role & User
                        </Typography>
                      </MenuItem>
                      <Divider />
                    </Collapse>
                    <MenuItem
                      key="logs"
                      onClick={handleCloseUserMenu}
                      component={Link}
                      to="/management/action-logs?range=week"
                    >
                      <Typography component={'div'} textAlign="center">
                        Action Logs
                      </Typography>
                    </MenuItem>
                  </>
                )}
                <LogoutButton onLogout={handleCloseUserMenu} />
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </Box>
    </AppBar>
  );
}

export default Header;
